import React, { useMemo, useState } from 'react';
import { Alert, Spinner } from '@digitools/honest-react';
import { useTranslation } from 'react-i18next';
import styles from './LeaveDetails.scss';
import { useExperience } from 'status/hooks';
import formatDateMMDDYYY from 'status/utilities/formatDateMMDDYYY';
import titleCase from 'status/utilities/titleCase';
import getMethodSent from 'status/utilities/getMethodSent';
import { useLanguage, useUiData } from 'status/hooks';
import CorrespondenceFilter from '../filters/CorrespondenceFilter';
import useGet from '../../../../../hooks/useGet';
import CorrespondenceCards from '../ClaimDetails/CorrespondenceCards';
import downloadPdf from 'status/utilities/downloadPdf';
import { TEALIUM_EVENT } from '../../../../../constants/tealiumConstants';
import useTealium from 'src/hooks/useTealium';

const Error = ({ error, noResults }) => {
  const notFound = noResults || (error.response && error.response.status === 404);
  return (
    <Alert type={notFound ? 'info' : 'error'}>
      {notFound ? (
        'There are no documents to display at this time.'
      ) : (
        <span>
          <strong>Error!</strong>&nbsp;Unable to load correspondence documents.
        </span>
      )}
    </Alert>
  );
};

const openPdfDocument = (get, href, title, i, id, trackEvent) => {
  get({
    url: `/status-service/correspondence/retrieve/document/${id}?docURL=${href}`,
    options: {
      headers: {
        Accept: 'application/pdf;',
        'Content-Type': 'application/json; charset=utf-8',
      },
      responseType: 'blob',
    },
    success: res => {
      trackEvent({
              event_action: TEALIUM_EVENT.EVENT_ACTION.DOWNLOAD,
              event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
              event_name: 'forms',
              event_version: 'correspondence - details'
            });
      downloadPdf(res, title);
    },
  });
};

const Correspondence = ({ leaveNum }) => {
  const { trackEvent } = useTealium();
  const { experience } = useExperience();
  const { language } = useLanguage();
  const { t } = useTranslation();

  const [filteredData, setFilteredData] = useState();

  const { error: uiDataError, loading: uiDataLoading, uiData } = useUiData();

  const { error, loading, data } = useGet({
    url: `/status-service/correspondence/search/documents/${experience}/LEAVE/ASO`,
    options: {
      params: {
        claimLeaveNum: leaveNum,
      },
    },
  });

  const {
    error: downloadError,
    loading: downloading,
    get,
  } = useGet({
    noInitial: true,
  });

  const formatDocToDisplay = doc => {
    return {
      ...doc,
      documentType: titleCase(doc.documentType),
      productTypeCode: titleCase(doc.productTypeCode),
      documentDate: formatDateMMDDYYY(new Date(doc.documentDate)),
      sentDate: doc.sentDate ? formatDateMMDDYYY(new Date(doc.sentDate)) : '',
      sentToDistributionMethod: getMethodSent(doc.sentToDistributionMethodCode, uiData),
    };
  };

  const formattedData = useMemo(() => {
    if (filteredData) {
      return filteredData.map(doc => formatDocToDisplay(doc));
    } else if (data) {
      return data._embedded.documentList.map(doc => formatDocToDisplay(doc));
    }
  }, [data, uiData, language, filteredData]);

  if (loading) {
    return <Spinner id='leave-correspondence-spinner' />;
  }

  const initialData = data ? data._embedded.documentList : undefined;
  const noResults = initialData && initialData.length === 0;

  if (error || noResults) {
    return <Error error={error} noResults={noResults} />;
  }

  return (
    <div className={styles.tableContainer}>
      <CorrespondenceFilter initialData={formattedData} setFilteredData={setFilteredData} />
      {!noResults && (
        <>
          <CorrespondenceCards openDocGet={get} openDoc={(get, href, title, i, id) => openPdfDocument(get, href, title, i, id, trackEvent)} correspondenceList={formattedData} />
          <span>{t('questionsCaseManagerText')}</span>
        </>
      )}
    </div>
  );
};

export default Correspondence;
